import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card } from '@nucleus-commerce/ui/components/card';

import { useOrganizationOptions } from '@/features/organizations';

import { auditLogParams } from '../api/mappers';
import { AuditLogFilters } from '../components/audit-log-filters';
import { AuditLogTable } from '../components/audit-log-table';
import { useAuditLog } from '../hooks/use-audit-log';

const ROUTE_ID = '/_authed/audit-log' as const;

function AuditLog() {
  const { t } = useTranslation('auditLog');
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: '/audit-log' });
  const params = useMemo(() => auditLogParams(search), [search]);
  const query = useAuditLog(params);
  const organizations = useOrganizationOptions();
  const names = useMemo(
    () => new Map((organizations.data ?? []).map((option) => [option.id, option.name])),
    [organizations.data],
  );
  const entries = query.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <PageContent>
      <PageHeader
        title={<span className="font-serif text-[1.875rem] font-normal">{t('title')}</span>}
        description={t('description')}
      />
      <Card className="mb-4 px-5 py-4" data-tour="platform.audit-log.filters">
        <AuditLogFilters
          search={search}
          onApply={(next) => void navigate({ search: next, replace: true })}
        />
      </Card>
      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <Card className="gap-0 overflow-hidden py-0" data-tour="platform.audit-log.table">
          <AuditLogTable entries={entries} organizationNames={names} loading={query.isPending} />
        </Card>
      )}
      {query.hasNextPage ? (
        <div className="flex justify-center pt-4">
          <Button
            variant="outline"
            disabled={query.isFetchingNextPage}
            onClick={() => void query.fetchNextPage()}
          >
            {query.isFetchingNextPage ? t('loadingMore') : t('loadMore')}
          </Button>
        </div>
      ) : null}
    </PageContent>
  );
}

export function AuditLogPage() {
  return (
    <FeatureErrorBoundary name="audit-log">
      <AuditLog />
    </FeatureErrorBoundary>
  );
}
